import React, { useState, useEffect } from 'react'
import Container from '@embracesbs/component-container'
import Button2 from '@embracesbs/component-button2'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import Checkbox from '@embracesbs/component-checkbox'
import Text from '@embracesbs/component-text'
import Modal from '@embracesbs/component-modal'
import EmptyState from '@embracesbs/component-emptystate'
import DarkTabs from '../Components/Common/DarkTabs'
import { DEVELOPER_CENTER_PATH } from '../Components/MainMenu/navigation'
import LoadingState from '../Components/Common/LoadingState'
import { useApi } from '../Hooks'

interface Tenant {
  id: string
  tenantName: string
  displayName: string
  environment: string
  status?: 'success' | 'failed' | null
}

interface BulkSelectMatch {
  input: string
  tenantId: string
  isExact: boolean
}

interface BulkSelectAmbiguity {
  input: string
  candidates: Tenant[]
}

interface BulkSelectResults {
  found: BulkSelectMatch[]
  ambiguous: BulkSelectAmbiguity[]
  notFound: string[]
}

const EMPTY_BULK_SELECT_RESULTS: BulkSelectResults = { found: [], ambiguous: [], notFound: [] }

const BULK_UPDATE_BATCH_SIZE = 5

const normalizeName = (value: string): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const findTenantMatches = (input: string, tenants: Tenant[]): { exact: Tenant[]; partial: Tenant[] } => {
  const needle = normalizeName(input)
  if (!needle) {
    return { exact: [], partial: [] }
  }

  // tenantName is the canonical identifier, so a match on it wins outright even if the same
  // text also happens to equal some other tenant's free-text displayName.
  const tenantNameExact = tenants.filter((tenant) => normalizeName(tenant.tenantName) === needle)
  if (tenantNameExact.length > 0) {
    return { exact: tenantNameExact, partial: [] }
  }

  const namesOf = (tenant: Tenant) => [normalizeName(tenant.tenantName), normalizeName(tenant.displayName)].filter(Boolean)

  const exact = tenants.filter((tenant) => namesOf(tenant).includes(needle))
  if (exact.length > 0) {
    return { exact, partial: [] }
  }

  const partial = tenants.filter((tenant) => namesOf(tenant).some((name) => name.includes(needle) || needle.includes(name)))
  return { exact: [], partial }
}

const BulkUpdate: React.FC = () => {
  const { fetchApi } = useApi()
  const [segments, setSegments] = useState<string[]>([])
  const [isLoadingSegments, setIsLoadingSegments] = useState(true)
  const [isLoadingTenants, setIsLoadingTenants] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState<string>('')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])
  const [version, setVersion] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [updateResults, setUpdateResults] = useState<{ id: string; status: 'success' | 'failed'; tenantId: string }[]>([])
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false)
  const [isBulkSelectModalOpen, setIsBulkSelectModalOpen] = useState(false)
  const [bulkSelectInput, setBulkSelectInput] = useState<string>('')
  const [bulkSelectResults, setBulkSelectResults] = useState<BulkSelectResults>(EMPTY_BULK_SELECT_RESULTS)
  // Keyed by the ambiguous input line, so a pick survives re-processing as long as that line is still ambiguous.
  const [ambiguousSelections, setAmbiguousSelections] = useState<Record<string, string>>({})
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)
  const [totalTenantsToUpdate, setTotalTenantsToUpdate] = useState<number>(0)
  const [tenantsProcessed, setTenantsProcessed] = useState<number>(0)
  const [currentBatch, setCurrentBatch] = useState<number>(0)
  const [totalBatches, setTotalBatches] = useState<number>(0)
  const [batchStatus, setBatchStatus] = useState<'idle' | 'running' | 'success' | 'failed'>('idle')

  useEffect(() => {
    const fetchSegments = async () => {
      setIsLoadingSegments(true)
      try {
        const response = await fetchApi({
          endpoint: `/api/featureflags/segments`,
          method: 'GET'
        })
        console.log('Segments response:', response)

        if (response && response.length > 0) {
          setSegments(response)
          setSelectedSegment(response[0])
        } else {
          setSegments([])
        }
      } catch (error) {
        console.error('Error fetching segments:', error)
        setSegments([])
      } finally {
        setIsLoadingSegments(false)
      }
    }

    fetchSegments()
  }, [fetchApi])

  useEffect(() => {
    const fetchTenants = async () => {
      if (!selectedSegment) {
        console.log('No segment selected')
        setTenants([])
        return
      }

      setIsLoadingTenants(true)
      try {
        console.log('Fetching tenants for segment:', selectedSegment)
        const response = await fetchApi({
          endpoint: `/api/tenant/environment/${encodeURIComponent(selectedSegment)}`,
          method: 'GET'
        })

        console.log('Raw API Response:', response)

        if (Array.isArray(response)) {
          console.log(`Found ${response.length} tenants`)
          setTenants(response)
        } else if (response && Array.isArray(response.data)) {
          console.log(`Found ${response.data.length} tenants`)
          setTenants(response.data)
        } else {
          console.log('No tenants found in response')
          setTenants([])
        }
      } catch (error) {
        console.error('Error fetching tenants:', error)
        setTenants([])
      } finally {
        setIsLoadingTenants(false)
      }
    }

    fetchTenants()
  }, [selectedSegment])

  useEffect(() => {
    console.log('Current tenants in state:', tenants)
  }, [tenants])

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [debounceTimer])

  const handleSegmentChange = (_: any, newSelected: string) => {
    if (newSelected === selectedSegment) {
      return
    }

    setSelectedSegment(newSelected)
    setSelectedTenants([])
    setTenants([])
    setSearchQuery('')
    setIsLoadingTenants(true)
  }

  console.log(segments)
  const handleTenantSelection = (tenantId: string) => {
    setSelectedTenants((prev) => (prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]))
  }

  const handleBulkSelect = () => {
    setIsBulkSelectModalOpen(true)
    setBulkSelectInput('')
    setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
    setAmbiguousSelections({})
  }

  const processBulkSelection = (inputValue?: string) => {
    const valueToProcess = inputValue !== undefined ? inputValue : bulkSelectInput
    if (!valueToProcess.trim()) {
      setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
      setAmbiguousSelections({})
      return
    }

    const inputTenants = valueToProcess
      .split(/[\n,]/)
      .map((name) => name.trim())
      .filter((name) => name.length > 0)

    if (inputTenants.length === 0) {
      setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
      setAmbiguousSelections({})
      return
    }

    const found: BulkSelectMatch[] = []
    const ambiguous: BulkSelectAmbiguity[] = []
    const notFound: string[] = []

    inputTenants.forEach((inputName) => {
      const { exact, partial } = findTenantMatches(inputName, tenants)
      const matches = exact.length > 0 ? exact : partial

      if (matches.length === 1) {
        found.push({ input: inputName, tenantId: matches[0].id, isExact: exact.length > 0 })
      } else if (matches.length > 1) {
        ambiguous.push({ input: inputName, candidates: matches })
      } else {
        notFound.push(inputName)
      }
    })

    setBulkSelectResults({ found, ambiguous, notFound })
    // Drop any prior pick whose line is no longer ambiguous, or whose candidate list changed underneath it.
    setAmbiguousSelections((prev) => {
      const next: Record<string, string> = {}
      ambiguous.forEach((entry) => {
        const prevChoice = prev[entry.input]
        if (prevChoice && entry.candidates.some((candidate) => candidate.id === prevChoice)) {
          next[entry.input] = prevChoice
        }
      })
      return next
    })
  }

  const handleAmbiguousSelect = (input: string, tenantId: string) => {
    setAmbiguousSelections((prev) => {
      if (prev[input] === tenantId) {
        const { [input]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [input]: tenantId }
    })
  }

  // A line only blocks selection while genuinely unresolved — once the user picks a candidate
  // for it below, it counts the same as a found match.
  const unresolvedAmbiguous = bulkSelectResults.ambiguous.filter((entry) => !ambiguousSelections[entry.input])
  const hasAmbiguousNames = unresolvedAmbiguous.length > 0

  // Two pasted lines can land on the same tenant; the button should count tenants, not lines.
  const uniqueFoundTenantIds = Array.from(
    new Set([...bulkSelectResults.found.map((match) => match.tenantId), ...Object.values(ambiguousSelections)])
  )

  const confirmBulkSelection = () => {
    setSelectedTenants((prev) => {
      const newSelection = [...prev]
      uniqueFoundTenantIds.forEach((tenantId) => {
        if (!newSelection.includes(tenantId)) {
          newSelection.push(tenantId)
        }
      })
      return newSelection
    })
    setIsBulkSelectModalOpen(false)
  }

  const handleSubmit = async () => {
    // The button is disabled in these cases; this is just a safety guard.
    if (!version || selectedTenants.length === 0) {
      return
    }
    // Reset previous run's results so the confirmation list starts clean
    setUpdateResults([])
    setIsConfirmModalOpen(true)
  }

  const handleConfirm = async () => {
    console.log('Starting bulk update for', selectedTenants.length, 'tenants')
    setIsLoading(true)
    setUpdateResults([])

    const tenantIdsToUpdate = [...selectedTenants]
    const total = tenantIdsToUpdate.length
    const batchesCount = Math.ceil(total / BULK_UPDATE_BATCH_SIZE)

    setTotalTenantsToUpdate(total)
    setTenantsProcessed(0)
    setCurrentBatch(0)
    setTotalBatches(batchesCount)
    setBatchStatus('idle')

    try {
      for (let batchIndex = 0; batchIndex < batchesCount; batchIndex++) {
        const batchTenantIds = tenantIdsToUpdate.slice(batchIndex * BULK_UPDATE_BATCH_SIZE, (batchIndex + 1) * BULK_UPDATE_BATCH_SIZE)

        setCurrentBatch(batchIndex + 1)
        setBatchStatus('running')
        console.log(`Sending bulk update request for batch ${batchIndex + 1}/${batchesCount} with ${batchTenantIds.length} tenants...`)

        try {
          const response = await fetchApi({
            endpoint: '/api/tenantversion/bulk-update',
            method: 'POST',
            body: {
              version,
              tenantIds: batchTenantIds,
              environment: selectedSegment
            }
          })

          console.log('Bulk update response received for batch', batchIndex + 1, response)
          const { updatedTenants = [], failedTenants = [] } = response || {}

          const batchResults = [
            ...updatedTenants.map((tenantId: string) => ({
              id: tenantId,
              status: 'success' as const,
              tenantId
            })),
            ...failedTenants.map((failureInfo: string) => {
              const [rawTenantId, ...rest] = failureInfo.split(':')
              const tenantId = (rawTenantId || '').trim()
              const message = rest.join(':').trim()
              const displayId = tenantId && message ? `${tenantId}: ${message}` : failureInfo

              return {
                id: displayId,
                status: 'failed' as const,
                tenantId: tenantId || failureInfo
              }
            })
          ]

          setUpdateResults((prev) => [...prev, ...batchResults])
          if ((failedTenants ?? []).length === 0) {
            setBatchStatus('success')
          } else {
            setBatchStatus('failed')
          }
        } catch (error: any) {
          console.error('Bulk update failed for batch', batchIndex + 1, error)
          const errorMessage = error?.message || 'Unknown error'

          const failedBatchResults = batchTenantIds.map((tenantId) => ({
            id: `${tenantId}: ${errorMessage}`,
            status: 'failed' as const,
            tenantId
          }))

          setUpdateResults((prev) => [...prev, ...failedBatchResults])
          setBatchStatus('failed')
        } finally {
          setTenantsProcessed((prev) => prev + batchTenantIds.length)
        }
      }

      setIsConfirmModalOpen(false) // Close the confirmation modal
      setIsResultsModalOpen(true) // Open the results modal
      console.log('Bulk update completed successfully across all batches')
    } catch (error) {
      console.error('Bulk update encountered an unexpected error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const tenantStatusByName: Record<string, 'success' | 'failed'> = {}
  updateResults.forEach((result) => {
    const key = (result.tenantId || result.id)?.toLowerCase()
    if (key) {
      tenantStatusByName[key] = result.status
    }
  })

  const selectedTenantDetails = tenants.filter((tenant) => selectedTenants.includes(tenant.id))

  // Same label the environment tabs show, so the modal matches the tab the user picked.
  const selectedSegmentLabel = selectedSegment ? selectedSegment.charAt(0).toUpperCase() + selectedSegment.slice(1) : ''

  const normalizedSearchQuery = normalizeName(searchQuery)
  const filteredTenants = tenants.filter(
    (tenant) =>
      normalizeName(tenant.displayName).includes(normalizedSearchQuery) || normalizeName(tenant.tenantName).includes(normalizedSearchQuery)
  )

  if (isLoadingSegments) {
    return (
      <div style={{ height: 'var(--app-height, 100vh)' }}>
        <LoadingState text='Loading environments…' />
      </div>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', height: 'var(--app-height, 100vh)', overflow: 'hidden' }}>
        {/* Left column: environment tabs + search / selection tools. */}
        <div style={{ width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DarkTabs
            tabs={segments.map((segment) => ({ id: segment, text: segment.charAt(0).toUpperCase() + segment.slice(1) }))}
            activeId={selectedSegment}
            onTabClick={(id) => handleSegmentChange(null, id)}
            backTo={DEVELOPER_CENTER_PATH}
          />
          {/* Flush white bar under the tabs, same pattern as the feature flags page. */}
          <div
            style={{
              flexShrink: 0,
              background: 'var(--tma-panel-bg)',
              borderBottom: '1px solid var(--tma-list-card-border)',
              padding: '10px'
            }}
          >
            <div className='search-no-glow' style={{ marginBottom: '8px' }}>
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search tenants'
                type='text'
                variant='default'
                isSearchMode
                onClear={() => setSearchQuery('')}
              />
            </div>
            <Button2
              text='Bulk select by name'
              onClick={handleBulkSelect}
              variant='outlined'
              buttonSize='30px'
              buttonRadius='8px'
              isFullWidth
              style={{ justifyContent: 'center' }}
            />
          </div>
          {/* Selection summary */}
          <div style={{ flex: 1, minHeight: 0, background: 'var(--tma-page-bg)', padding: '12px 10px' }}>
            <Text textStyle='description' textColor='grey'>
              {`${selectedTenants.length} of ${tenants.length} tenants selected`}
            </Text>
            {selectedTenants.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <Button2 text='Clear selection' onClick={() => setSelectedTenants([])} variant='subtle' buttonSize='30px' />
              </div>
            )}
          </div>
        </div>

        {/* Right column: version + update action in a 52px band, tenant grid below. */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--tma-panel-bg)',
            padding: '0 20px',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ flexShrink: 0, height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <Text textStyle='h6' style={{ fontSize: '16px' }}>
                Select tenants to update
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '240px' }}>
                  <TextField
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder='Version (e.g. r62sp202507)'
                    type='text'
                    variant='default'
                  />
                </div>
                <Button2
                  text={isLoading ? 'Updating…' : `Update selected (${selectedTenants.length})`}
                  onClick={handleSubmit}
                  isDisabled={selectedTenants.length === 0 || !version.trim() || isLoading}
                  variant='contained'
                  buttonSize='30px'
                  buttonRadius='8px'
                />
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--tma-list-card-border)', margin: '0 -20px' }} />
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 0' }} className='custom-scrollbar'>
            {isLoadingTenants ? (
              <LoadingState text={`Loading tenants for ${selectedSegment}…`} />
            ) : tenants.length === 0 ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                  imageName='emptyfolder'
                  size='medium'
                  title='No tenants found'
                  text={`No tenants exist in ${selectedSegment}.`}
                />
              </div>
            ) : filteredTenants.length === 0 ? (
              <Text align='center' textColor='grey'>
                No tenants match '{searchQuery}'
              </Text>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '8px' }}>
                {filteredTenants.map((tenant: Tenant) => {
                  const isSelected = selectedTenants.includes(tenant.id)
                  return (
                    <div
                      key={tenant.id}
                      style={{
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: isSelected ? 'var(--tma-accent)' : 'var(--tma-list-card-border)',
                        borderRadius: '6px',
                        background: 'var(--tma-panel-bg)',
                        padding: '8px 12px',
                        transition: 'border-color 0.15s ease'
                      }}
                    >
                      <Checkbox
                        name={tenant.id}
                        label={tenant.displayName}
                        description={tenant.tenantName}
                        isChecked={isSelected}
                        onClick={() => handleTenantSelection(tenant.id)}
                        value='a'
                        isLabelSingleLine
                        isDescriptionSingleLine
                        innerSpacing={0}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isConfirmModalOpen}
        width='45%'
        onClose={() => {
          if (!isLoading) {
            setIsConfirmModalOpen(false)
          }
        }}
        onCancel={() => {
          if (!isLoading) {
            setIsConfirmModalOpen(false)
          }
        }}
        hasHeaderBorder={true}
        headerTitle='Confirm Bulk Version Update'
        height='auto'
        onConfirm={handleConfirm}
        variant='confirm'
        labelConfirm='Update'
        labelCancel='Cancel'
        isConfirmDisabled={isLoading}
      >
        <Container isVertical spacing={20}>
          <Text>
            You are about to update the following tenants to version <strong>{version}</strong>:
          </Text>

          <Container isVertical spacing={8}>
            {selectedTenantDetails.map((tenant) => {
              const nameKey = tenant.tenantName?.toLowerCase()
              const idKey = tenant.id?.toLowerCase()
              const status = tenantStatusByName[nameKey] ?? tenantStatusByName[idKey]
              const color = status === 'success' ? 'var(--tma-success-text)' : status === 'failed' ? 'var(--tma-danger-text)' : undefined

              return (
                <Text key={tenant.id} style={color ? { color } : undefined}>
                  • {tenant.displayName} ({tenant.tenantName})
                </Text>
              )
            })}
          </Container>

          <Text>Are you sure you want to proceed?</Text>

          {isLoading && totalTenantsToUpdate > 0 && (
            <Text
              textColor={batchStatus === 'success' || batchStatus === 'failed' ? undefined : 'grey'}
              style={
                batchStatus === 'success'
                  ? { color: 'var(--tma-success-text)' }
                  : batchStatus === 'failed'
                    ? { color: 'var(--tma-danger-text)' }
                    : undefined
              }
            >
              Updating tenants... Batch {currentBatch} of {totalBatches}. Processed {tenantsProcessed} of {totalTenantsToUpdate}.
            </Text>
          )}
        </Container>
      </Modal>
      <Modal
        isOpen={isResultsModalOpen}
        width='70%'
        onClose={() => setIsResultsModalOpen(false)}
        hasHeaderBorder={true}
        headerTitle='Bulk Update Results'
        height='auto'
        labelConfirm='Close'
        onConfirm={() => setIsResultsModalOpen(false)}
      >
        <Container isVertical spacing={20}>
          <Text>The results of the bulk update are as follows:</Text>

          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid var(--tma-card-border)',
              borderRadius: '4px',
              padding: '16px',
              backgroundColor: 'var(--tma-card-bg)'
            }}
          >
            <Container isVertical spacing={8}>
              {updateResults.map((result) => (
                <Text
                  key={result.id}
                  style={{
                    color: result.status === 'success' ? 'var(--tma-success-text)' : 'var(--tma-danger-text)',
                    fontWeight: result.status === 'success' ? 'normal' : 'bold',
                    wordBreak: 'break-word',
                    lineHeight: '1.4'
                  }}
                >
                  • {result.id} - {result.status === 'success' ? 'Updated' : 'Failed'}
                </Text>
              ))}
            </Container>
          </div>
        </Container>
      </Modal>
      <Modal
        isOpen={isBulkSelectModalOpen}
        width='50%'
        onClose={() => {
          setIsBulkSelectModalOpen(false)
          setBulkSelectInput('')
          setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
          setAmbiguousSelections({})
        }}
        onCancel={() => {
          setIsBulkSelectModalOpen(false)
          setBulkSelectInput('')
          setBulkSelectResults(EMPTY_BULK_SELECT_RESULTS)
          setAmbiguousSelections({})
        }}
        hasHeaderBorder={true}
        headerTitle={`Bulk Select Tenants — ${selectedSegmentLabel}`}
        height='auto'
        onConfirm={confirmBulkSelection}
        variant='confirm'
        labelConfirm={`Select ${uniqueFoundTenantIds.length} Tenant${uniqueFoundTenantIds.length !== 1 ? 's' : ''}`}
        labelCancel='Cancel'
        isConfirmDisabled={uniqueFoundTenantIds.length === 0 || hasAmbiguousNames}
      >
        <Container isVertical spacing={20}>
          {/* Names only match tenants in the active environment tab, so state that up front. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              border: '1px solid var(--tma-list-card-border)',
              borderRadius: '6px',
              background: 'var(--tma-page-bg)',
              padding: '8px 12px'
            }}
          >
            <span
              style={{
                display: 'inline-block',
                background: 'var(--tma-accent)',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '2px 10px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.4px'
              }}
            >
              {selectedSegmentLabel}
            </span>
            <Text textStyle='description' textColor='grey'>
              {`Matching against the ${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} in ${selectedSegmentLabel}. Switch the environment tab to select tenants from another stage.`}
            </Text>
          </div>

          <Text>Enter tenant names or display names, separated by commas or new lines:</Text>

          <TextFieldWrapper labelText='Tenant Names' style={{ width: '100%' }} spacing={0}>
            <textarea
              value={bulkSelectInput}
              onChange={(e) => {
                const newValue = e.target.value
                const oldValue = bulkSelectInput
                setBulkSelectInput(newValue)

                if (debounceTimer) {
                  clearTimeout(debounceTimer)
                }

                if (newValue.trim() === '') {
                  processBulkSelection(newValue)
                  return
                }

                const lengthDiff = Math.abs(newValue.length - oldValue.length)
                if (lengthDiff > 10) {
                  setTimeout(() => processBulkSelection(newValue), 50)
                } else {
                  const newTimer = setTimeout(() => processBulkSelection(newValue), 300)
                  setDebounceTimer(newTimer)
                }
              }}
              placeholder='Example:&#10;tenant-name-1&#10;tenant-name-2&#10;tenant-name-3'
              style={{
                width: '100%',
                height: '120px',
                padding: '8px',
                border: '1px solid var(--tma-card-border)',
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </TextFieldWrapper>

          {bulkSelectResults.ambiguous.length > 0 && (
            <div style={{ border: '1px solid #f59e0b', borderRadius: '6px', background: '#fffbeb', padding: '10px 12px' }}>
              <Text style={{ fontWeight: 'bold', color: '#92400e' }}>
                {unresolvedAmbiguous.length > 0
                  ? `${unresolvedAmbiguous.length} name${unresolvedAmbiguous.length !== 1 ? 's' : ''} match more than one tenant. Pick the right one below, or make the name unique.`
                  : `${bulkSelectResults.ambiguous.length} name${bulkSelectResults.ambiguous.length !== 1 ? 's' : ''} matched more than one tenant — resolved below.`}
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {bulkSelectResults.ambiguous.map((entry, index) => {
                  const selectedTenantId = ambiguousSelections[entry.input]
                  // Only worth an inline picker when the candidate list is small; beyond that, make the user retype.
                  const canPickInline = entry.candidates.length <= 3
                  return (
                    <div key={`${entry.input}-${index}`} style={{ marginLeft: '8px' }}>
                      <Text style={{ color: '#92400e', fontSize: '12px', marginBottom: '4px' }}>'{entry.input}' matches:</Text>
                      {canPickInline ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {entry.candidates.map((candidate) => {
                            const isChosen = selectedTenantId === candidate.id
                            return (
                              <button
                                key={candidate.id}
                                type='button'
                                onClick={() => handleAmbiguousSelect(entry.input, candidate.id)}
                                style={{
                                  cursor: 'pointer',
                                  border: `1px solid ${isChosen ? 'var(--tma-accent)' : '#f59e0b'}`,
                                  borderRadius: '14px',
                                  padding: '4px 10px',
                                  fontSize: '12px',
                                  background: isChosen ? 'var(--tma-accent)' : '#ffffff',
                                  color: isChosen ? '#ffffff' : '#92400e'
                                }}
                              >
                                {isChosen ? '✓ ' : ''}
                                {candidate.displayName} ({candidate.tenantName})
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <Text style={{ color: '#92400e', fontSize: '12px' }}>
                          {entry.candidates.map((candidate) => candidate.displayName).join(', ')} — too many matches to pick inline, make
                          the name more specific.
                        </Text>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {(bulkSelectResults.found.length > 0 || hasAmbiguousNames || bulkSelectResults.notFound.length > 0) && (
            <>
              <Text textColor='grey'>
                Parsed {bulkSelectResults.found.length + bulkSelectResults.ambiguous.length + bulkSelectResults.notFound.length} name
                {bulkSelectResults.found.length + bulkSelectResults.ambiguous.length + bulkSelectResults.notFound.length !== 1 ? 's' : ''}:
                found {bulkSelectResults.found.length}, ambiguous {bulkSelectResults.ambiguous.length}, not found{' '}
                {bulkSelectResults.notFound.length}.
              </Text>
              <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text style={{ fontWeight: 'bold', color: 'var(--tma-success-text)' }}>
                    ✓ Found in {selectedSegmentLabel} ({bulkSelectResults.found.length})
                  </Text>
                  <div
                    style={{
                      height: '150px',
                      overflowY: 'scroll',
                      border: '1px solid var(--tma-card-border)',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: 'var(--tma-success-tint)'
                    }}
                  >
                    {bulkSelectResults.found.length > 0 ? (
                      bulkSelectResults.found.map((match, index) => {
                        const tenant = tenants.find((t) => t.id === match.tenantId)
                        return tenant ? (
                          <div key={`${match.input}-${match.tenantId}-${index}`} style={{ marginLeft: '8px', marginBottom: '4px' }}>
                            <Text style={{ color: 'var(--tma-success-text)' }}>
                              • {tenant.displayName} ({tenant.tenantName})
                            </Text>
                            {!match.isExact && (
                              <Text style={{ marginLeft: '10px', color: 'var(--tma-success-text)', fontSize: '12px' }}>
                                matched on '{match.input}'
                              </Text>
                            )}
                          </div>
                        ) : null
                      })
                    ) : (
                      <Text style={{ color: 'var(--tma-text-muted)', fontStyle: 'italic' }}>No tenants found yet</Text>
                    )}
                  </div>
                </div>

                <div style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text style={{ fontWeight: 'bold', color: 'var(--tma-danger-text)' }}>
                    ✗ Not found in {selectedSegmentLabel} ({bulkSelectResults.notFound.length})
                  </Text>
                  <div
                    style={{
                      height: '150px',
                      overflowY: 'scroll',
                      border: '1px solid var(--tma-card-border)',
                      borderRadius: '4px',
                      padding: '8px',
                      backgroundColor: 'var(--tma-danger-tint)'
                    }}
                  >
                    {bulkSelectResults.notFound.length > 0 ? (
                      bulkSelectResults.notFound.map((name) => (
                        <Text key={name} style={{ marginLeft: '8px', color: 'var(--tma-danger-text)', marginBottom: '4px' }}>
                          • {name}
                        </Text>
                      ))
                    ) : (
                      <Text style={{ color: 'var(--tma-text-muted)', fontStyle: 'italic' }}>All tenants found</Text>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Container>
      </Modal>
    </>
  )
}

export default BulkUpdate
